import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "@lyder-talk/core/lib/db-client";
import { useToast } from "@/hooks/use-toast";
import { sendDiscordWebhook } from "@/lib/discord-webhook";
import { ResetPasswordForm, AuthField } from "@lyder-talk/ui";
import { consumeAuthEmailToken, describeAuthEmailError } from "@lyder-talk/core";
import { User } from "lucide-react";
import { auth } from "@lyder-talk/core/lib/auth-client";

/**
 * Finalises an invited account: redeems the invite token, then collects a password
 * and an optional display name. Previously this lived on the www subdomain and only
 * called `getSession()`, so it broke for anyone whose link had been consumed by a
 * mail scanner or challenged by Cloudflare before the session could be established.
 */
const AcceptInvite = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isValidating, setIsValidating] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const hasProcessedToken = useRef(false);

  useEffect(() => {
    if (hasProcessedToken.current) return;
    hasProcessedToken.current = true;

    const acceptInvite = async () => {
      try {
        await consumeAuthEmailToken("invite");

        const { data: { session } } = await auth.getSession();
        if (!session) {
          throw new Error("Could not start your session. Please request a new invite.");
        }

        setUserEmail(session.user.email || "");
        setIsValidating(false);
      } catch (err) {
        console.error("Error validating invite link:", err);
        toast({
          title: "Invalid Invitation",
          description: describeAuthEmailError(
            err,
            "This invitation link is invalid. Please request a new one."
          ),
          variant: "destructive",
        });
        navigate("/auth/signin", { replace: true });
      }
    };

    acceptInvite();
  }, [navigate, toast]);

  const handleSubmit = async (password: string) => {
    const { error } = await auth.updateUser({ password });
    if (error) throw new Error(error.message);

    if (displayName.trim()) {
      const { data: { user } } = await auth.getUser();
      if (user) {
        const { error: profileError } = await db.from("profiles")
          .update({
            display_name: displayName.trim(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", user.id);

        // A failed profile update should not block an otherwise finished signup.
        if (profileError) console.error("Error updating profile:", profileError);
      }
    }

    sendDiscordWebhook({
      eventType: "user_signup",
      email: userEmail,
      timestamp: new Date().toISOString(),
    });

    toast({
      title: "Account Created!",
      description: "Welcome to Lyder Talk. Your account is ready.",
    });
    navigate("/", { replace: true });
  };

  if (isValidating) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <p className="text-muted-foreground animate-pulse">Validating invitation...</p>
      </div>
    );
  }

  return (
    <ResetPasswordForm
      onSubmit={handleSubmit}
      title="Welcome to Lyder Talk"
      description={userEmail ? `Finalize your account for ${userEmail}` : "Finalize your account"}
      passwordLabel="Set Password"
      confirmPasswordLabel="Confirm Password"
      submitLabel="Complete Setup"
      submittingLabel="Setting up..."
    >
      <AuthField
        name="invite-display-name"
        label="Display name (optional)"
        icon={<User className="h-[18px] w-[18px]" />}
        value={displayName}
        onChange={setDisplayName}
        placeholder="Your name"
        autoComplete="name"
      />
    </ResetPasswordForm>
  );
};

export default AcceptInvite;
